/**
 * The six numbers worth knowing before opening any table.
 *
 * Gap A phase 2b: these rendered in the legacy Management body, which meant
 * they showed under all seven category tabs. They are Ad Health numbers, so
 * they belong on Ad Review — where the table they summarise already lives.
 * The migration registry had already marked "Ad Health" as deleted while
 * these six tiles were still on the page below it, which is how the banner
 * came to under-report what was left (Art, 2026-09-22).
 *
 * ⚠ Each tile shows "—" rather than 0 when its read failed. A zero here would
 * claim "no problems found" on the strength of a read that never happened,
 * which is the same lie as an empty table.
 */
import type {
  AdMonitorResponse,
  ChangeHistoryResponse,
  ConversionViewResponse,
} from '@/services/googleAdsManagementService';

import { budgetHeadline } from './managementFormat';

export function AdHealthSummaryTiles({
  ads,
  conversions,
  changes,
  onFocus,
}: {
  ads?: AdMonitorResponse;
  conversions?: ConversionViewResponse;
  changes?: ChangeHistoryResponse;
  onFocus?: (f: 'waste' | 'disapproved') => void;
}) {
  const tile = (
    label: string,
    value: string,
    tone: 'bad' | 'plain',
    hint: string,
    scope: string,
    // ⚠ Only tiles that can actually narrow a table below are clickable. A
    // tile that looks clickable and does nothing is worse than a plain one.
    onClick?: () => void,
  ) => (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={
        'rounded-md border p-3 ' +
        (onClick
          ? 'cursor-pointer transition-colors hover:border-coral focus:outline-none focus-visible:ring-2 focus-visible:ring-ring '
          : '') +
        (tone === 'bad' && value !== '—' && value !== '0'
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-border')
      }
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>
      {/* ⛔ THE UNIVERSE THE NUMBER WAS DRAWN FROM. Viktor: "the cards lie by
          silence" — a bare "0" does not distinguish "scanned 55 ads and found
          none" from "scanned nothing". Every tile states what it looked at and
          over what window, so a zero is a finding rather than an absence. */}
      <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground/70">
        {scope}
      </p>
    </div>
  );
  const n = (state: { read_ok: boolean } | undefined, v: number | undefined) =>
    state?.read_ok && v !== undefined ? String(v) : '—';
  const scope = (state: { read_ok: boolean } | undefined, universe: string) =>
    state?.read_ok ? universe : 'not read';
  const adCount = ads?.ads.length ?? 0;
  const primaryCount =
    conversions?.actions?.filter((a) => a.primary_for_goal).length ?? 0;
  const money = (v: number | undefined) =>
    ads?.read_ok && v !== undefined
      ? `$${Math.round(v).toLocaleString()}`
      : '—';

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {tile('Ad groups with nothing serving', n(ads, ads?.dark_ad_groups.length), 'bad',
        'Live keywords that can win nothing',
        scope(ads, `of ${adCount} ads · last ${ads?.window_days ?? 30}d`))}
      {tile('Disapproved ads', n(ads, ads?.disapproved_count), 'bad',
        'Not serving at all',
        scope(ads, `of ${adCount} ads · last ${ads?.window_days ?? 30}d`),
        ads?.read_ok && (ads?.disapproved_count ?? 0) > 0
          ? () => onFocus?.('disapproved')
          : undefined)}
      {/* ⛔ SPEND THAT BOUGHT NOTHING — the one tile that is an outcome.
          Viktor on the first version: "it shows spend and cosmetics, not
          outcome." Impressions and Ad Strength say nothing an operator can
          act on; dollars against zero conversions do. */}
      {tile('Spend with no conversions', money(ads?.waste_spend), 'bad',
        `${n(ads, ads?.waste_count)} ad(s) spending and converting nothing`,
        scope(ads, `of ${adCount} ads · last ${ads?.window_days ?? 30}d`),
        ads?.read_ok && (ads?.waste_count ?? 0) > 0
          ? () => onFocus?.('waste')
          : undefined)}
      {/* ⛔ The GBP tile replaced a "silent conversion actions" tile. Silence
          fires on ~63% of the fleet and is a cleanup backlog, not an alarm.
          GBP actions marked primary is specific, verified on 14 of 27 accounts,
          and means the conversion column itself is wrong. */}
      {tile('GBP actions counted as leads', n(conversions, conversions?.gbp_primary_count), 'bad',
        'Directions and Menu views inflating conversions',
        scope(conversions, `of ${conversions?.actions?.length ?? 0} actions · last ${conversions?.window_days ?? 30}d`))}
      {/* ⛔ THE FINDING THAT MOVES MONEY, and it was on another page. Smart
          Bidding optimises toward primary conversion actions; a primary that
          has recorded nothing in 29 days is a signal that stopped arriving,
          and bidding keeps chasing it. Viktor asked for this tile by name. */}
      {tile('Primaries recording nothing', n(conversions, conversions?.silent_count), 'bad',
        'Bidding is optimising toward a signal that stopped',
        scope(conversions, `of ${primaryCount} primary actions · last ${conversions?.window_days ?? 30}d`))}
      {/* ⚠ "API LIMIT" USED TO SIT UNDER THIS NUMBER UNCONDITIONALLY, which
          read as "the count is truncated" — Viktor: "27 is a floor, not a
          count". It never was: the row cap is 500 and this account returned 35.
          The window cap (29 days, Google's own) is the real constraint, and the
          count only becomes a floor when the backend says it was truncated. */}
      {tile(
        'Budget changes',
        changes?.read_ok
          ? `${changes.budget_changes.length}${changes.truncated ? '+' : ''}`
          : '—',
        'plain',
        'Changes that moved money',
        scope(
          changes,
          changes?.truncated
            ? 'last 29d · more than shown'
            : `last ${changes?.window_days ?? 29}d · Google's max window`,
        ),
      )}
    </div>
  );
}

